'use client';

import { useCallback, useMemo, useState } from 'react';

import type { PurchasedAvatarMeMediaItem } from '@ps/api/types';
import { type Listing, type WithID } from '@ps/models';
import { useCurrentUserId } from '@ps/ui/contexts/auth';

import type { CarouselMedia } from '../../pdp/components/ProductImageCarousel';
import { isWearWithAiSuitable } from '../../products/functions/listingCardImage';
import { buildPdpCarouselMedia } from '../functions/buildPdpCarouselMedia';
import { useNavigateToWearWithAi } from '../hooks/useNavigateToWearWithAi';
import { usePurchasedAvatarMeListingMedia } from '../hooks/usePurchasedAvatarMeListingMedia';
import {
  useToggleAvatarMeMediaFavorite,
  useToggleAvatarMeMediaVisibility,
} from '../hooks/useToggleAvatarMeMedia';
import { WearWithAiCta } from './WearWithAiCta';
import { WearWithAiSlideChips } from './WearWithAiSlideChips';

/**
 * Owns the Wear-With-AI integration for the PDP. Exposes:
 *  - `cta`: the green CTA button (creator-only) for the carousel's
 *    `floatingActions` slot.
 *  - `media`: the merged carousel slide list to pass into
 *    `ProductImageCarousel.media`. For the creator this includes
 *    their API-fetched AI media; for the viewer it's the listing's
 *    curated `defaultWearWithAiMedia` + `extraWearWithAiMedias`.
 *  - `slideOverlay`: a `(slide) => ReactNode` for the carousel's
 *    `slideOverlay` prop. Returns the favorite + visibility chips
 *    when the active slide is the creator's own AI media; null
 *    otherwise.
 *
 * 1:1 with legacy `ListingImageContainer.tsx` + `ListingImage
 * CarouselAffiliateOverlay.tsx`.
 */
export function usePdpWearWithAi({
  listing,
  sellerId,
}: {
  listing: WithID<Listing>;
  sellerId: string;
}): {
  cta: React.ReactNode;
  media: CarouselMedia[];
  slideOverlay: (slide: CarouselMedia, index: number) => React.ReactNode;
} {
  const currentUserId = useCurrentUserId();
  const navigateToWear = useNavigateToWearWithAi();

  const isOwner = Boolean(currentUserId && currentUserId === sellerId);
  const eligible = isWearWithAiSuitable(listing);

  // Fetch the creator's own AI media only for the owner — viewers
  // see listing-level curated media (no API call).
  const { rows: avatarMedia, refresh } = usePurchasedAvatarMeListingMedia({
    sellerId,
    listingId: listing.id,
    enabled: eligible && isOwner,
  });

  // Optimistic overlay for favorite + visibility — apply on top of
  // the API rows so the chip reflects the user's intent without
  // waiting for the network roundtrip.
  const [optimistic, setOptimistic] = useState<
    Record<string, { favorite?: boolean; visible?: boolean }>
  >({});
  const [pendingId, setPendingId] = useState<string | null>(null);

  const toggleFavorite = useToggleAvatarMeMediaFavorite();
  const toggleVisibility = useToggleAvatarMeMediaVisibility();

  // Apply optimistic overrides on top of the API rows BEFORE
  // building the carousel media list.
  const enrichedAvatarMedia: PurchasedAvatarMeMediaItem[] = useMemo(() => {
    if (!isOwner || avatarMedia.length === 0) return avatarMedia;
    return avatarMedia.map(item => {
      const o = optimistic[item.id];
      if (!o) return item;
      return {
        ...item,
        wearWithAi: {
          listingId: item.wearWithAi?.listingId ?? '',
          favorite: o.favorite ?? item.wearWithAi?.favorite ?? false,
          visible: o.visible ?? item.wearWithAi?.visible ?? true,
        },
      };
    });
  }, [avatarMedia, isOwner, optimistic]);

  const media = useMemo<CarouselMedia[]>(() => {
    if (!eligible) {
      // Fall back to plain default + extra images — never any AI
      // media for ineligible listings.
      return buildPdpCarouselMedia({
        listing,
        isOwner: false,
        avatarMedia: [],
      });
    }
    return buildPdpCarouselMedia({
      listing,
      isOwner,
      avatarMedia: enrichedAvatarMedia,
    });
  }, [eligible, isOwner, listing, enrichedAvatarMedia]);

  const onPressFavorite = useCallback(
    async (slide: CarouselMedia) => {
      if (!slide.id) return;
      const next = !slide.favorite;
      setOptimistic(prev => ({
        ...prev,
        [slide.id!]: { ...prev[slide.id!], favorite: next },
      }));
      setPendingId(slide.id);
      const ok = await toggleFavorite({
        sellerId,
        id: slide.id,
        favorite: next,
      });
      setPendingId(null);
      if (!ok) {
        // Revert.
        setOptimistic(prev => ({
          ...prev,
          [slide.id!]: { ...prev[slide.id!], favorite: !next },
        }));
      } else {
        await refresh();
      }
    },
    [refresh, sellerId, toggleFavorite],
  );

  const onPressVisibility = useCallback(
    async (slide: CarouselMedia) => {
      if (!slide.id) return;
      const next = !slide.visible;
      setOptimistic(prev => ({
        ...prev,
        [slide.id!]: { ...prev[slide.id!], visible: next },
      }));
      setPendingId(slide.id);
      const ok = await toggleVisibility({
        sellerId,
        id: slide.id,
        visible: next,
      });
      setPendingId(null);
      if (!ok) {
        setOptimistic(prev => ({
          ...prev,
          [slide.id!]: { ...prev[slide.id!], visible: !next },
        }));
      } else {
        await refresh();
      }
    },
    [refresh, sellerId, toggleVisibility],
  );

  const cta = useMemo(() => {
    if (!eligible || !isOwner) return null;
    return (
      <WearWithAiCta
        onClick={() =>
          navigateToWear({
            listingId: listing.id,
            outfitImageUrl: listing.defaultImage,
          })
        }
      />
    );
  }, [eligible, isOwner, listing.defaultImage, listing.id, navigateToWear]);

  const slideOverlay = useCallback(
    (slide: CarouselMedia): React.ReactNode => {
      // Chips only show for the creator on their own AI-tagged
      // slides — mirrors legacy `currentMedia?.tag === 'avatar-me'`
      // + the outer `isOwner` gate.
      if (!isOwner || !eligible) return null;
      if (slide.tag !== 'avatar-me' || !slide.id) return null;
      return (
        <WearWithAiSlideChips
          slide={slide}
          onToggleFavorite={() => void onPressFavorite(slide)}
          onToggleVisibility={() => void onPressVisibility(slide)}
          pending={pendingId === slide.id}
        />
      );
    },
    [eligible, isOwner, onPressFavorite, onPressVisibility, pendingId],
  );

  return { cta, media, slideOverlay };
}
